/**
 * Section Template Library
 * Browse, save, and apply reusable section templates
 */

import React, { useState } from 'react';
import {
  Plus,
  Search,
  LayoutGrid,
  List,
  LayoutTemplate,
  Trash2,
  Download,
  Eye,
} from 'lucide-react';
import type { SectionTemplate } from '../../types';

// ============================================================================
// TYPES
// ============================================================================

interface SectionTemplateLibraryProps {
  templates: SectionTemplate[];
  onApply: (template: SectionTemplate) => void;
  onSave?: (template: Partial<SectionTemplate>) => Promise<void>;
  onDelete?: (templateId: string) => Promise<void>;
}

// ============================================================================
// TEMPLATE CARD
// ============================================================================

interface TemplateCardProps {
  template: SectionTemplate;
  onApply: () => void;
  onDelete?: () => void;
  viewMode: 'grid' | 'list';
}

function TemplateCard({ template, onApply, onDelete, viewMode }: TemplateCardProps) {
  if (viewMode === 'list') {
    return (
      <div className="flex items-center gap-4 p-3 border rounded-lg hover:border-blue-300 transition-colors group">
        {/* Thumbnail placeholder */}
        <div className="w-20 h-14 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
          {template.thumbnailUrl ? (
            <img
              src={template.thumbnailUrl}
              alt={template.templateName}
              className="w-full h-full object-cover rounded"
            />
          ) : (
            <LayoutTemplate className="h-6 w-6 text-gray-400" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900 truncate">
            {template.templateName}
          </h3>
          <p className="text-xs text-gray-500 truncate">
            {template.description || `${template.blocks.length} blocks`}
          </p>
          {template.category && (
            <span className="inline-block mt-1 text-xs bg-gray-100 px-2 py-0.5 rounded">
              {template.category}
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onApply}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
          >
            Apply
          </button>
          {onDelete && (
            <button
              onClick={onDelete}
              className="p-1.5 hover:bg-red-100 rounded"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </button>
          )}
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="border rounded-lg overflow-hidden hover:border-blue-300 transition-colors group">
      {/* Thumbnail */}
      <div className="aspect-video bg-gray-100 flex items-center justify-center relative">
        {template.thumbnailUrl ? (
          <img
            src={template.thumbnailUrl}
            alt={template.templateName}
            className="w-full h-full object-cover"
          />
        ) : (
          <LayoutTemplate className="h-8 w-8 text-gray-400" />
        )}

        {/* Overlay actions */}
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={onApply}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
          >
            Apply
          </button>
          <button className="p-1.5 bg-white/90 rounded hover:bg-white">
            <Eye className="h-4 w-4 text-gray-700" />
          </button>
        </div>
      </div>

      <div className="p-3">
        <h3 className="text-sm font-medium text-gray-900 truncate">
          {template.templateName}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5 truncate">
          {template.description || `${template.blocks.length} blocks`}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// SAVE TEMPLATE DIALOG
// ============================================================================

interface SaveDialogProps {
  onSave: (data: { name: string; description: string; category: string }) => void;
  onCancel: () => void;
}

function SaveDialog({ onSave, onCancel }: SaveDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-96">
        <h3 className="text-lg font-semibold mb-4">Save as Section Template</h3>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Hero with CTA"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description..."
              className="w-full border rounded-lg px-3 py-2 text-sm h-20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Uncategorized</option>
              <option value="hero">Hero</option>
              <option value="features">Features</option>
              <option value="testimonials">Testimonials</option>
              <option value="pricing">Pricing</option>
              <option value="cta">Call to Action</option>
              <option value="content">Content</option>
              <option value="footer">Footer</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave({ name, description, category })}
            disabled={!name}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            Save Template
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function SectionTemplateLibrary({
  templates,
  onApply,
  onSave,
  onDelete,
}: SectionTemplateLibraryProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Get unique categories
  const categories = Array.from(new Set(templates.map(t => t.category).filter(Boolean)));

  // Filter templates
  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !searchQuery ||
      t.templateName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !selectedCategory || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSave = async (data: { name: string; description: string; category: string }) => {
    if (onSave) {
      await onSave({
        templateName: data.name,
        description: data.description,
        category: data.category,
        blocks: [],
      });
    }
    setShowSaveDialog(false);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <LayoutTemplate className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold">Section Templates</h2>
          <span className="text-sm text-gray-500">{templates.length} templates</span>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="border-b px-4 py-3 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search templates..."
            className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
          />
        </div>

        {categories.length > 0 && (
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        )}

        <div className="flex items-center border rounded-lg">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 ${viewMode === 'grid' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
          >
            <List className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Template Grid/List */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredTemplates.length === 0 ? (
          <div className="text-center py-16">
            <LayoutTemplate className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {searchQuery ? 'No templates match your search' : 'No section templates yet'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onApply={() => onApply(template)}
                onDelete={onDelete ? () => onDelete(template.id) : undefined}
                viewMode="grid"
              />
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onApply={() => onApply(template)}
                onDelete={onDelete ? () => onDelete(template.id) : undefined}
                viewMode="list"
              />
            ))}
          </div>
        )}
      </div>

      {/* Save Dialog */}
      {showSaveDialog && (
        <SaveDialog onSave={handleSave} onCancel={() => setShowSaveDialog(false)} />
      )}
    </div>
  );
}

export default SectionTemplateLibrary;
